import { ZfIcon } from '@zafire/react';
import type { Moment } from 'moment';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { MONTH_12_VALUE } from '../../../config/constants';
import type { ITooltipContent } from '../../../interfaces/chart';
import type { RoadmapEvent } from '../../../interfaces/event';
import { formatCashFlowAmount } from '../../../utils/renderUtils';
import { TooltipSeries } from '../chart/TooltipSeries';
import { CashEventCard } from './CashEventCard';

import '../../../../styles/css-components/cash-event-tooltip.scss';

interface IProps {
    events: RoadmapEvent[];
    handleClose: () => void;
    maxEvents?: number;
    tooltipContent: ITooltipContent;
    openModal?: (event: RoadmapEvent) => void;
}

export const CashEventTooltipContent: FunctionComponent<IProps> = ({ events, handleClose, maxEvents, tooltipContent, openModal }) => {
    const { t } = useTranslation();

    const getYearForEvents = (time: Moment) => {
        if (time.month() === MONTH_12_VALUE) return time.year() + 1;

        return time.year();
    };

    const showNegative = (type: string) => type === 'line' || type === 'spline';

    return (
        <div className="cash-event-tooltip-content">
            <div className="cash-event-tooltip-content__header">
                <div className="cash-event-tooltip-content__title">{tooltipContent.title}</div>
                <ZfIcon className="cash-event-tooltip-content__close-btn" size="large" name="close" onClick={handleClose} />
            </div>
            <div className="cash-event-tooltip-content__series">
                {tooltipContent.series.map((series) => (
                    <TooltipSeries
                        key={series.name}
                        isLineChart={series.type === 'line'}
                        showNegative={showNegative(series.type)}
                        name={series.name}
                        value={Number(series.value)}
                        color={series.color}
                    />
                ))}
            </div>
            {events.length > 0 && (
                <>
                    <div className="cash-event-tooltip-content__envents-title">{t('plannedEventsIn', { year: getYearForEvents(tooltipContent.time) })}</div>
                    <div className="cash-event-tooltip-content__events">
                        {events.slice(0, maxEvents).map((event, index) => (
                            <CashEventCard
                                onOpenModalClick={() => openModal?.(event)}
                                topicKey={event.topicKey ?? ''}
                                key={event.name ?? '' + index}
                                isClickPopover={!maxEvents}
                                amount={maxEvents ? maxEvents : events.length}
                                title={event.name ?? ''}
                                description={formatCashFlowAmount(event.signedAmount!, event.type!, event.currency!)}
                            />
                        ))}
                    </div>
                </>
            )}
            {maxEvents && events.length > maxEvents && (
                <div className="cash-event-tooltip-content__hidden">
                    {events.length > maxEvents ? t('moreHidden', { amount: events.length - maxEvents }) : ''}
                </div>
            )}
        </div>
    );
};
